import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { announcementsService } from '../api/announcements.service';
import type { CreateAnnouncementInput, UpdateAnnouncementInput } from '../types';

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.announcements.all });
}

export function useAnnouncementMutations() {
  const qc = useQueryClient();
  const onError = useErrorToast();

  const createAnnouncement = useMutation({
    mutationFn: (input: CreateAnnouncementInput) => announcementsService.create(input),
    onSuccess: () => {
      invalidateAll(qc);
      toast.success('Comunicado criado');
    },
    onError: (e) => onError(e, 'Erro ao criar comunicado'),
  });

  const updateAnnouncement = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAnnouncementInput }) =>
      announcementsService.update(id, data),
    onSuccess: () => {
      invalidateAll(qc);
      toast.success('Comunicado atualizado');
    },
    onError: (e) => onError(e, 'Erro ao atualizar comunicado'),
  });

  const publishAnnouncement = useMutation({
    mutationFn: (id: string) => announcementsService.publish(id),
    onSuccess: (result) => {
      invalidateAll(qc);
      toast.success(
        `Comunicado publicado para ${result.recipientCount} empresa${result.recipientCount === 1 ? '' : 's'}`
      );
    },
    onError: (e) => onError(e, 'Erro ao publicar comunicado'),
  });

  const archiveAnnouncement = useMutation({
    mutationFn: (id: string) => announcementsService.archive(id),
    onSuccess: () => {
      invalidateAll(qc);
      toast.success('Comunicado arquivado');
    },
    onError: (e) => onError(e, 'Erro ao arquivar comunicado'),
  });

  const deleteAnnouncement = useMutation({
    mutationFn: (id: string) => announcementsService.remove(id),
    onSuccess: () => {
      invalidateAll(qc);
      toast.success('Comunicado excluído');
    },
    onError: (e) => onError(e, 'Erro ao excluir comunicado'),
  });

  const uploadMedia = useMutation({
    mutationFn: (file: File) => announcementsService.uploadMedia(file),
    onError: (e) => onError(e, 'Erro ao enviar imagem'),
  });

  return {
    createAnnouncement,
    updateAnnouncement,
    publishAnnouncement,
    archiveAnnouncement,
    deleteAnnouncement,
    uploadMedia,
  };
}
